/**
 * « Corriger l'orthographe » : envoie le texte au modèle, montre la version
 * corrigée en regard, et n'écrit rien tant qu'on n'a pas cliqué « Appliquer ».
 */
import { useState } from "react";
import { toast } from "sonner";
import { SpellCheck, Loader2, Check, X } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";

/** Paragraphes HTML à partir d'un texte corrigé (la mise en forme fine n'est pas conservée). */
export const textToHtml = (text) => (text || "").split(/\n{2,}/).map((p) => `<p>${p.split("\n").map(escapeHtml).join("<br>")}</p>`).join("");
const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export default function ProofreadButton({ text, onApply, className = "" }) {
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);   // {corrected, changed, provider}
  const run = async () => {
    if (!(text || "").trim()) { toast.info("Rien à corriger : la note est vide"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/notes/proofread", { text });
      if (!data.changed) { toast.success("Aucune faute détectée"); setProposal(null); }
      else setProposal(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Correction indisponible"); }
    finally { setLoading(false); }
  };
  return (
    <div className={className}>
      <button type="button" data-testid="proofread-btn" onClick={run} disabled={loading}
        title="Corriger l'orthographe et la grammaire (proposition, à appliquer ou non)"
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1 text-[12px] text-slate-600 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50">
        {loading ? <Loader2 size={13} className="animate-spin" /> : <SpellCheck size={13} />} Corriger l'orthographe
      </button>
      {proposal && (
        <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/50 p-2.5" data-testid="proofread-proposal">
          <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>Proposition{proposal.provider ? ` (${proposal.provider})` : ""} — relisez avant d'appliquer</span>
            <button type="button" onClick={() => setProposal(null)} aria-label="Ignorer"><X size={13} /></button>
          </div>
          <p className="whitespace-pre-wrap text-[13px] text-slate-800" data-testid="proofread-text">{proposal.corrected}</p>
          <div className="mt-2 flex gap-2">
            <button type="button" data-testid="proofread-apply" onClick={() => { onApply(proposal.corrected, textToHtml(proposal.corrected)); setProposal(null); }}
              className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-[12px] font-medium text-white hover:bg-indigo-700">
              <Check size={12} /> Appliquer
            </button>
            <button type="button" onClick={() => setProposal(null)} className="rounded-md px-2 py-1 text-[12px] text-slate-500 hover:text-slate-800">Ignorer</button>
          </div>
        </div>
      )}
    </div>
  );
}
